// src/contexts/PedidoContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import cardapioApi from '../components/Products/Cardapio/services/api';

/**
 * Shape esperado do pedido:
 * {
 *   id, restaurante_id, mesa_comanda, status: 'aberto'|'enviado'|...,
 *   total, itens: [{ id, item_id, quantidade, preco_unitario, opcoes_selecionadas, observacoes, item: {...} }]
 * }
 */

const PedidoContext = createContext(null);

export const PedidoProvider = ({ children }) => {
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  /**
   * Carrega (ou cria) um pedido "aberto" para a mesa indicada.
   * - Persiste a mesa em localStorage para reuso no front.
   */
  const carregarPedido = useCallback(async (restauranteId, mesa) => {
    if (!mesa) return null;
    setLoading(true);
    try {
      localStorage.setItem('mesaAtual', String(mesa));

      // tenta buscar pedido aberto
      let data = null;
      try {
        data = await cardapioApi.getPedidoPorMesa(mesa);
      } catch {
        data = null;
      }

      // se não existe ou não está aberto, cria novo
      if (!data || data.status !== 'aberto') {
        data = await cardapioApi.criarPedido({ restauranteId, mesa });
      }

      setPedido(data);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Adiciona um item ao pedido atual e recarrega o pedido para refletir total/itens.
   * payload: { id: itemDoCardapioId, quantidade, preco, opcoes, observacoes }
   */
  const adicionarItem = useCallback(async (payload) => {
    if (!pedido) return;
    await cardapioApi.adicionarItemPedido(pedido.id, payload);
    const atualizado = await cardapioApi.getPedidoPorMesa(pedido.mesa_comanda);
    setPedido(atualizado);
  }, [pedido]);

  /**
   * Envia o pedido atual (muda status no back) e tenta recarregar.
   */
  const enviarPedido = useCallback(async () => {
    if (!pedido) return null;
    setSending(true);
    try {
      const res = await cardapioApi.enviarPedido(pedido.id);
      const atualizado = await cardapioApi.getPedidoPorMesa(pedido.mesa_comanda).catch(() => null);
      if (atualizado) setPedido(atualizado);
      return res;
    } finally {
      setSending(false);
    }
  }, [pedido]);

  const value = {
    pedido,
    loading,
    sending,
    carregarPedido,
    adicionarItem,
    enviarPedido,
  };

  return <PedidoContext.Provider value={value}>{children}</PedidoContext.Provider>;
};

export const usePedido = () => useContext(PedidoContext);